"use client";
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/axios";

// Feature gating per paket (Sprint 10). Membaca paket tenant dari /api/billing/status:
//   - gated_menus   = semesta kode menu ter-gate (union semua paket)
//   - allowed_menus = kode menu ter-gate yang DIIZINKAN paket tenant ini
// FE menyembunyikan menu ter-gate yang TIDAK ada di allowed_menus (irisan dgn permission user).
// Fail-open: bila data paket tidak tersedia (tenant legacy/app/tanpa subscription) → tidak membatasi.

interface PlanMenusState {
    ready: boolean;
    gated: Set<string>;
    allowed: Set<string>;
    unrestricted: boolean; // true → tidak ada gating (legacy/app/no subscription/error)
}

const CACHE_KEY = "_planMenus";

export function usePlanMenus(): { planAllows: (code: string) => boolean } {
    const [state, setState] = useState<PlanMenusState>({
        ready: false,
        gated: new Set(),
        allowed: new Set(),
        unrestricted: true,
    });

    useEffect(() => {
        if (typeof window === "undefined") return;

        // Cache sesi (di-clear saat logout, lihat Sidebar.handleLogout)
        try {
            const cached = sessionStorage.getItem(CACHE_KEY);
            if (cached) {
                const p = JSON.parse(cached);
                setState({
                    ready: true,
                    gated: new Set(p.gated || []),
                    allowed: new Set(p.allowed || []),
                    unrestricted: !!p.unrestricted,
                });
                return;
            }
        } catch { /* ignore corrupt cache */ }

        api.get("/billing/status")
            .then((res) => {
                const sub = res.data?.subscription;
                const gated: string[] = Array.isArray(sub?.gated_menus) ? sub.gated_menus : [];
                const allowed: string[] = Array.isArray(sub?.allowed_menus) ? sub.allowed_menus : [];
                // Tanpa daftar gated → tidak ada gating (mis. tenant app/legacy/no subscription).
                const unrestricted = !sub || gated.length === 0;
                const val = { gated, allowed, unrestricted };
                try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(val)); } catch { /* quota */ }
                setState({ ready: true, gated: new Set(gated), allowed: new Set(allowed), unrestricted });
            })
            .catch(() => {
                // Fail-open: jangan sembunyikan apa pun bila gagal resolve paket.
                setState({ ready: true, gated: new Set(), allowed: new Set(), unrestricted: true });
            });
    }, []);

    const planAllows = useCallback(
        (code: string): boolean => {
            if (!state.ready) return true;          // masih loading → fail-open (tampilkan semua)
            if (state.unrestricted) return true;    // tidak ada gating
            if (!state.gated.has(code)) return true; // menu core (di luar semesta ter-gate)
            return state.allowed.has(code);          // menu ter-gate → hanya jika diizinkan paket
        },
        [state]
    );

    return { planAllows };
}
